/*
 *   File : listg.js
 *   Author : https://evoqins.com
 *   Description : List all non kyc nri of rayi
 *   Integrations : 'react-toastify'
 *   Version : 1.0.0
*/

// custom packages
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

// custom components
import { CustomDatePicker, CustomSelectBox } from '../../Components/FormElements';
import { PrimaryButton, TextButton, SecondaryButton } from '../../Components/Buttons';
import { CustomDataTable } from '../../Components/DataTable';
import { Chip, EmptyScreen } from '../../Components/Others';
import { Icon } from '../../Components/Icon';
import { APIService } from '../../Service';
import { CheckStatusModal } from '../../Components/Modal';

// custom style
import color from '../../Style/color.scss';
import searchInput from '../../Components/FormElements/search-input';
import { Modal } from 'bootstrap';

const NonKyc = () => {

    const navigate = useNavigate();
    const isRendered = useRef(true);
    const scrollRef = useRef(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [onPaginationChange, setOnPaginationChange] = useState(false);

    const [toggleFilter, setToggleFilter] = useState(false);
    const [selectedType, setSelectedType] = useState(null);
    const [filterData, setFilterData] = useState({});
    const [status, setStatus] = useState([]);
    const [nriList, setNriList] = useState({});

    const [selectedDate, setSelectedDate] = useState(null);
    const [calendarToggle, setCalendarToggle] = useState(false);
    const [dateError, setDateError] = useState("");
    const [loader, setLoader] = useState(true);

    const [updatedSelectedDate, setUpdateSelectedDate] = useState(null);
    const [updatedCalendarToggle, setUpdatedCalendarToggle] = useState(false);
    const [updatedDateError, setUpdatedDateError] = useState("");

    const [checkStatus, setCheckStatus] = useState(false);
    const [checked, setChecked] = useState([]);

    const [kycStatusData, setKycStatusData] = useState([]);
    const [checkStatusLoader, setCheckStatusLoader] = useState(false);
    const [downloadLoader, setDownloadLoader] = useState(false);

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    }, []);

    useEffect(() => {
        if (checkStatus === true) {
            const modal = new Modal(document.getElementById("check-status-modal"));
            modal.show();
        }
    }, [checkStatus]);

    useEffect(() => {
        if (Object.keys(filterData).length !== 0) {
            if (filterData.status) {
                const updated_status = [{ value: null, label: 'All' }, ...filterData.status];
                setFilterData(updated_status)
                setStatus(updated_status);
                setSelectedType(updated_status[0])
            }
        }

    }, [filterData])


    useEffect(() => {
        if (isRendered.current === true) {
            _filter();
            _nriList();
        }
        isRendered.current = false;
    }, [isRendered.current]);

    // handle table page change 
    const _handleTablePageChange = (page_num) => {
        setCurrentPage(page_num);
        _nriList(page_num);
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
    };

    // handle select date 
    const _handleSelectDate = (day) => {
        setSelectedDate(day);
        setCalendarToggle(false);
    };
    // handle select date 
    const _handleSelectDateUpdated = (day) => {
        setUpdateSelectedDate(day);
        setUpdatedCalendarToggle(false);
    };

    // handle clear date
    const _handleClearDate = () => {
        setSelectedDate(null);
    };
    // handle clear date
    const _handleClearDateUpdated = () => {
        setUpdateSelectedDate(null);
    };

    // handle toggle date picker 
    const _handleToggleCalendar = () => {
        setCalendarToggle(!calendarToggle);
    };
    // handle toggle date picker 
    const _handleToggleCalendarUpdated = () => {
        setUpdatedCalendarToggle(!updatedCalendarToggle);
    };

    // handle navigate to order detail 
    const _handleNavigate = (customer_id) => {
        navigate(`${customer_id}`);
    };

    // handle toggle filter 
    const _handleToggleFilter = () => {
        setToggleFilter(prev => !prev);
    };

    // handle detail
    const _handleView = (customer_id) => {
        navigate(`${customer_id}`);
    }

    // handle select filter 
    const _handleSelectFilter = (selected) => {
        setSelectedType(selected);
    };

    const _handleFilter = () => {
        _nriList();
        setLoader(true);
    };

    // handle clear 
    const _handleClear = () => {
        setSelectedType(null);
        setSelectedDate(null);
        setUpdateSelectedDate(null);
        setLoader(true);
        isRendered.current = true;
    };

    // handle check status
    const _handleCheckStatus = () => {

        if (checked?.length === 0) {
            toast.dismiss();
            toast.error("Please select at least one customer", { type: 'error' });
            return;
        }

        _checkStatus();
    };

    const _toggleAllRows = () => {
        const current_ids = nriList.data.map(row => row.customer_id);
        const new_checked_ids = [...checked];

        const updated_selected = current_ids.every(id => checked.includes(id));

        if (updated_selected) {
            // Uncheck current page
            setChecked(new_checked_ids.filter(id => !current_ids.includes(id)));
        } else {
            // Check current page
            setChecked(Array.from(new Set([...new_checked_ids, ...current_ids])));
        }
    };

    const _handleChecked = (customer_id) => {
        setChecked(prev => {
            if (prev.includes(customer_id)) {
                return prev.filter(id => id !== customer_id);
            } else {
                return [...prev, customer_id]
            }
        })
    };

    // Order data table column
    const th = (label) => <span className="e-table-th-wrap">{label}</span>;
    const thLines = (...lines) => (
        <span className="e-table-th-wrap">
            {lines.map((line, i) => (
                <Fragment key={i}>
                    {i > 0 ? <br /> : null}
                    {line}
                </Fragment>
            ))}
        </span>
    );

    const NON_KYC_NRI_TABLE_COLUMN = [
        {
            name: (
                nriList.data &&
                <Icon
                    icon={nriList.data.every(row => checked.includes(row.customer_id)) ? 'checkbox-active' : 'checkbox-inactive'} // Toggle based on selection
                    size={16}
                    className='cursor-pointer'
                    onClick={() => _toggleAllRows()}
                />
            ),
            cell: (row) => (
                <Icon
                    icon={checked?.includes(row.customer_id) ? 'checkbox-active' : 'checkbox-inactive'}
                    size={16}
                    onClick={() =>
                        _handleChecked(row.customer_id)
                    }
                />
            ),
            width: "52px",
            grow: 0,
            id: "nri_select",
        },
        {
            name: th('Name'),
            selector: row => row.name,
            cell: (row) =>
                <span className='e-text-blue-violet text-decoration-hover-underline text-truncate cursor-pointer d-block'
                    title={row.name || undefined}
                    onClick={() => _handleNavigate(row.customer_id)}>
                    {row.name || "-"}
                </span>,
            width: "300px",
            sortable: true,
            grow: 1,
            id: "nri_name",
        },

        {
            name: th('Customer ID'),
            selector: row => row.rayi_customer_id,
            cell: (row) =>
                <span className='e-text-blue-violet text-decoration-hover-underline text-truncate cursor-pointer d-block'
                    title={row.rayi_customer_id || undefined}
                    onClick={() => _handleNavigate(row.customer_id)}>
                    {row.rayi_customer_id || "-"}
                </span>,
            width: "150px",
            sortable: true,
            grow: 0,
            id: "nri_customer_id",
        },
        {
            name: th('Email'),
            cell: (row) =>
                <a href={`mailto:${row.email}`} target='_blank' title={row.email || undefined} className='e-text-smoky-black text-decoration-none text-decoration-hover-underline text-truncate d-block'>
                    {row.email}
                </a>,
            width: "300px",
            grow: 0,
            id: "nri_email",
        },
        {
            name: thLines('Phone', 'number'),
            cell: (row) =>
                row.phone ?
                    <a href={`tel:${row.phone}`} title={row.phone} className='e-text-smoky-black text-decoration-none text-decoration-hover-underline text-truncate d-block'>
                        {row.phone}
                    </a>
                    : <span>-</span>,
            width: "140px",
            grow: 0,
            id: "nri_phone",
        },
        {
            name: th('PAN'),
            selector: row => row.pan,
            cell: row => <span className='text-truncate d-block' title={row.pan || undefined}>{row.pan || "-"}</span>,
            width: "120px",
            grow: 0,
            id: "nri_pan",
        },
        {
            name: th('Status'),
            cell: (row) =>
                <Chip label={row.status}
                    className={row.status === "Opened" ? "e-bg-pale-lavender-dark  e-text-dark-gray" :
                        row.status === "KYC verified" ? "e-bg-light-green  e-text-dark-gray" : ""} />,
            width: "100px",
            grow: 0,
            id: "nri_status",
        },
        {
            name: thLines('Submitted', 'date'),
            selector: row => row.intiated_on !== null ? row.intiated_on : "-",
            cell: row => <span className='text-truncate d-block' title={row.intiated_on || undefined}>{row.intiated_on || "-"}</span>,
            width: "160px",
            sortable: true,
            // selector: row => new Date(row.intiated_on),
            format: row => row.intiated_on,
            grow: 0,
            id: "nri_submitted_date",
        },
        {
            name: thLines('Internal', 'Status'),
            cell: (row) =>
                <Chip label={row.internal_status}
                    className={row.internal_status.toLowerCase() === "verified" ? "e-bg-green-crayola" : "e-bg-marigold"} />,
            width: "120px",
            grow: 0,
            id: "nri_internal_status",
        },
        {
            name: thLines('KRA', 'Status'),
            cell: (row) =>
                <>
                    {row.kra_status ?
                        <Chip label={row.kra_status}
                            className={row.kra_status?.toLowerCase() === "success" ? "e-bg-green-crayola" : row.kra_status?.toLowerCase() === "failed" ? "e-bg-maximum-red" : row.kra_status?.toLowerCase() === "pending" ? "e-bg-marigold" : "e-bg-bleu-de-france"}
                        />
                        :
                        "-"}
                </>,
            width: "120px",
            grow: 0,
            id: "nri_kra_status",
        },
        {
            name: th('Updated on'),
            selector: row => row.updated_on || "-",
            cell: row => <span className='text-truncate d-block' title={row.updated_on || undefined}>{row.updated_on || "-"}</span>,
            width: "130px",
            sortable: true,
            format: row => row.updated_on,
            grow: 0,
            id: "nri_updated_on",
        },
        {
            name: '',
            cell: (row) =>
                <TextButton name=' View details'
                    className='e-hover'
                    onClick={() => _handleView(row.customer_id)} />,
            width: "130px",
            grow: 0,
            id: "nri_action",
        },
    ];

    // API -filter api
    const _filter = () => {
        let is_post = false;
        let url = 'non-kyc-user/filter-data';
        APIService(is_post, url).then((response) => {
            if (response.status_code === 200) {
                setFilterData(response.data)
            } else {
                setFilterData([])
                // toast.dismiss();
                // toast.error(response.message, { type: 'error' })
            }
        })
    };


    // API -Non-kyc nri list
    const _nriList = (page_num = currentPage) => {

        const formatted_date = selectedDate === null ? null : format(new Date(selectedDate), 'yyyy-MM-dd');
        const updated_on = updatedSelectedDate === null ? null : format(new Date(updatedSelectedDate), 'yyyy-MM-dd');

        let is_post = true;
        let url = 'non-kyc-user/list';
        let request = {
            "query": "",
            "page_num": page_num,
            "page_size": 10,
            "status": selectedType !== null ? selectedType.value : null,
            "kyc_intiated_on": formatted_date,
            "updated_on": updated_on
        }
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                setNriList(response.data)
            } else {
                setNriList([]);
            }
            setLoader(false);
            setOnPaginationChange(false);
        })
    };

    // API - Check customer status
    const _checkStatus = () => {

        let is_post = true;
        let url = 'non-kyc-user/check-kyc-status';
        let request = {
            customer_ids: checked
        }

        setCheckStatusLoader(true);

        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                setCheckStatus(true);
                setKycStatusData(response.data);
            } else {
                toast.dismiss();
                toast.error(response.message, { type: 'error' })
            }
            setCheckStatusLoader(false);
        })
    };

    const _downloadList = () => {
        const formatted_date = selectedDate === null ? null : format(new Date(selectedDate), 'yyyy-MM-dd');
        const updated_on = updatedSelectedDate === null ? null : format(new Date(updatedSelectedDate), 'yyyy-MM-dd');

        let is_post = true;
        let url = 'non-kyc-user/userlist-download';
        let request = {
            "query": "",
            "status": selectedType !== null ? selectedType.value : null,
            "kyc_intiated_on": formatted_date,
            "updated_on": updated_on,
            customer_ids: checked
        }
        setDownloadLoader(true);
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                // window.open(response.data.url, '_blank');

                const url = response.data.url;
                const link = document.createElement('a');
                link.href = url;
                link.download = ''; // Optional: specify a file name
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                setChecked([]);
            } else {
                toast.dismiss();
                toast.success(response.message, { type: "error" })
            }
            setDownloadLoader(false);
        })
    };

    return (
        <Fragment>
            <section className='e-section-height pb-5' >
                <div className='e-bg-lavender padding-52px-lr pt-2 padding-112px-bottom'>
                    <h3 className='mb-0 e-text-chinese-black e-primary-font e-font-24 e-font-weight-600 line-height-24px'>
                        Non KYC NRI’s
                    </h3>
                </div>

                <div className='padding-52px-lr margin--80px-top'>
                    <div ref={scrollRef} className='e-bg-ghost-white border-radius-16 '>
                        <div className='padding-32px-lr padding-32px-top'>

                            <div className=' d-flex align-items-center justify-content-between '>

                                <div className='e-primary-font e-font-16 line-height-20px e-text-smoky-black e-font-weight-600'>Customers list</div>

                                <div className='d-flex align-items-center justify-content-end gap-4'>
                                    <div className={`${toggleFilter ? 'e-bg-blue-violet' : ''} padding-10px-all border-radius-8 e-cursor-pointer e-border-1px-light-purple cursor-pointer`}
                                        onClick={_handleToggleFilter}>
                                        <Icon icon='filter'
                                            size='1.25rem'
                                            className="d-flex"
                                            color={toggleFilter && color.white} />
                                    </div>
                                    <SecondaryButton name="Download List"
                                        disabled={loader || nriList?.data?.length === 0 ? true : false}
                                        loader={downloadLoader}
                                        className={downloadLoader ? "w-129px h-42px" : ""}
                                        onClick={_downloadList} />
                                    <PrimaryButton name="Check the status"
                                        paddingBlock='padding-12px-tb padding-24px-lr'
                                        loader={checkStatusLoader}
                                        className={checkStatusLoader ? 'w-147px' : ''}
                                        onClick={_handleCheckStatus} />
                                </div>
                            </div>
                        </div>

                        {/* Filter section */}
                        {toggleFilter &&
                            <div className='mt-2 e-bg-main-white padding-24px-tb padding-32px-lr position-relative'>
                                <div className='position-absolute right-32px top-16px e-primary-font e-font-14 line-height-16px e-font-weight-600 e-text-blue-violet cursor-pointer'
                                    onClick={_handleClear}>Clear all</div>
                                <div className='d-flex gap-4 padding-32px-bottom '>
                                    {/* Type select */}
                                    <CustomSelectBox title='Status'
                                        placeholder='Select'
                                        width='15rem'
                                        options={status}
                                        isSearchable={false}
                                        value={selectedType}
                                        onChange={(data) => _handleSelectFilter(data)} />

                                    {/* kyc initiated date select */}
                                    <CustomDatePicker label="Submitted date"
                                        placeholder="Select "
                                        selected={selectedDate}
                                        type={2}
                                        toggle={calendarToggle}
                                        error={dateError}
                                        handleToggle={_handleToggleCalendar}
                                        handleClose={_handleToggleCalendar}
                                        handleClear={_handleClearDate}
                                        handleSelect={_handleSelectDate} />

                                    {/* Updated on date select */}
                                    <CustomDatePicker label="Updated on date"
                                        placeholder="Select "
                                        selected={updatedSelectedDate}
                                        type={2}
                                        toggle={updatedCalendarToggle}
                                        error={updatedDateError}
                                        handleToggle={_handleToggleCalendarUpdated}
                                        handleClose={_handleToggleCalendarUpdated}
                                        handleClear={_handleClearDateUpdated}
                                        handleSelect={_handleSelectDateUpdated} />
                                </div>

                                <PrimaryButton name='Filter now'
                                    paddingBlock='padding-12px-tb padding-44px-lr'
                                    onClick={_handleFilter} />
                            </div>
                        }

                        {nriList.length !== 0 && loader === false &&
                            <div className='pt-3 padding-32px-lr d-flex gap-1 align-items-center justify-content-end '>
                                <div>
                                    <img src={require("../../Assets/Image/scroll-animation.gif")}
                                        draggable={false}
                                        className='e-rotate-270'
                                        width={24}
                                        height={24}
                                        alt='scroll' />
                                </div>
                                <p className='mb-0 e-text-bleu-de-france e-alt-font e-font-12 e-font-weight-500 line-height-12px'>
                                    Scroll horizontally for view more  information
                                </p>
                            </div>
                        }

                        {/* Order list table */}
                        {loader === true ?
                            <div className='d-flex align-items-center min-vh-50 justify-content-center'>
                                <img src={require("../../Assets/Image/loader.gif")}
                                    draggable={false}
                                    width={80}
                                    height={80}
                                    alt='loader'
                                    className='text-center' />
                            </div>
                            :
                            nriList.length !== 0 ?
                                <div className='e-data-table-wrapper-default-updated pt-3 padding-32px-bottom'>
                                    <CustomDataTable columns={NON_KYC_NRI_TABLE_COLUMN}
                                        data={nriList.data}
                                        pagination={true}
                                        paginationDefaultPage={nriList.current_page}
                                        paginationPerPage={10}
                                        paginationTotalRows={nriList.total_count}
                                        lastPage={nriList.total_page}
                                        onChangePage={_handleTablePageChange}
                                    // rowClick={(row) => _handleNavigate(row.customer_id)}
                                    />
                                </div>
                                :
                                <EmptyScreen
                                    image={require('../../Assets/Image/NotFound/no-data.png')}
                                    title='No results found'
                                    description={`We couldn't find anything that matches your filter.`}
                                />
                        }
                    </div>
                </div>
            </section>

            {checkStatus === true &&
                <CheckStatusModal
                    type={1}
                    data={kycStatusData}
                    handleSuccess={() => {
                        setTimeout(() => {
                            setCheckStatus(false);
                            setChecked([]);
                        }, 200);
                    }} />
            }
        </Fragment>
    )
}

export default NonKyc;
